import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQualificationRecords } from '../../qualifications/useQualificationRecords';
import { qualificationStatus, QUALIFICATION_STATUS_LABELS } from '../../qualifications/status';
import { CHOOSE_OPTION_LABEL, EMPTY_VALUE_LABEL } from '../../../../utils/formFieldPresentation';

const TAG_CATEGORY_NONE_OPTION = '__no_tag_in_category__';
const BLANK_MATCH_MODES = new Set(['is_blank', 'is_not_blank']);

function normalizeText(value) {
  return String(value ?? '').trim();
}

function normalizeDisplayValue(value) {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (value?.toDate?.() instanceof Date) return value.toDate().toLocaleDateString('en-GB');
  if (Array.isArray(value)) return value.map(normalizeDisplayValue).filter(Boolean).join(', ');
  if (typeof value === 'object') return normalizeText(JSON.stringify(value));
  return normalizeText(value);
}

function normalizeOptions(options) {
  return Array.isArray(options)
    ? options.map((opt) => normalizeText(opt)).filter(Boolean)
    : [];
}

function normalizeFieldType(type) {
  const t = normalizeText(type).toLowerCase();
  if (['number', 'date'].includes(t)) return t;
  if (['select', 'yesno', 'checkbox'].includes(t)) return 'select';
  return 'text';
}

function mergeOptions(existing = [], next = []) {
  return Array.from(new Set([
    ...normalizeOptions(existing),
    ...normalizeOptions(next),
  ])).sort((a, b) => a.localeCompare(b));
}

function getJoinAnswerValue(answer) {
  if (answer && typeof answer === 'object' && !Array.isArray(answer) && Object.prototype.hasOwnProperty.call(answer, 'value')) {
    return answer.value;
  }
  return answer;
}

function buildAnswerRows(member = {}, profileLabelById = new Map()) {
  const rows = [];
  const profileFields = member?.profileFields && typeof member.profileFields === 'object'
    ? member.profileFields
    : {};

  Object.entries(profileFields).forEach(([fieldId, value]) => {
    const label = profileLabelById.get(fieldId) || fieldId;
    rows.push({
      key: `profile:${fieldId}`,
      kind: 'profile',
      fieldId,
      source: 'Profile',
      label,
      value: normalizeDisplayValue(value),
    });
  });

  const joinAnswers = member?.joinAnswers && typeof member.joinAnswers === 'object'
    ? member.joinAnswers
    : {};

  Object.entries(joinAnswers).forEach(([questionId, answer]) => {
    const label = normalizeText(answer?.label) || questionId;
    rows.push({
      key: `join:${questionId}`,
      kind: 'join',
      fieldId: questionId,
      source: 'Join',
      label,
      value: normalizeDisplayValue(getJoinAnswerValue(answer)),
    });
  });

  return rows.sort((a, b) => a.source.localeCompare(b.source) || a.label.localeCompare(b.label));
}

function normalizeMemberTags(rawTags) {
  if (Array.isArray(rawTags)) {
    return rawTags.map((tag) => {
      if (typeof tag === 'string') return tag.trim();
      if (tag && typeof tag === 'object') return normalizeText(tag.name);
      return '';
    }).filter(Boolean);
  }

  if (rawTags && typeof rawTags === 'object') {
    const out = [];
    Object.entries(rawTags).forEach(([tagName, value]) => {
      const normalizedTagName = normalizeText(tagName);
      if (!normalizedTagName) return;

      if (value === true || value === 1 || value === 'true') {
        out.push(normalizedTagName);
        return;
      }
      if (value === false || value == null || value === '') return;

      const values = Array.isArray(value) ? value : [value];
      values.forEach((entry) => {
        if (entry == null || entry === '') return;
        if (typeof entry === 'object' && entry.name) {
          const nestedName = normalizeText(entry.name);
          if (nestedName) out.push(nestedName);
          return;
        }
        if (typeof entry === 'string') {
          const nestedName = normalizeText(entry);
          if (nestedName) out.push(nestedName);
        }
      });
    });
    return Array.from(new Set(out)).sort((a, b) => a.localeCompare(b));
  }

  return [];
}

function csvEscape(value) {
  if (value == null) return '';
  const text = String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function buildFileName(teamName) {
  const safeBase = normalizeText(teamName)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const date = new Date().toISOString().slice(0, 10);
  return `${safeBase || 'team'}-member-search-${date}.csv`;
}

function triggerCsvDownload(csvText, fileName) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function buildMemberSearchText(member = {}, profileLabelById = new Map()) {
  const parts = [
    normalizeText(member?.name),
    ...normalizeMemberTags(member?.tags),
  ];

  buildAnswerRows(member, profileLabelById).forEach((row) => {
    parts.push(row.source, row.label, row.value);
  });

  return parts.join(' ').toLowerCase();
}

function getFilterValue(member = {}, filter = {}) {
  if (filter.kind === 'tag') return normalizeMemberTags(member?.tags).join(' ');

  if (filter.kind === 'join') {
    const joinAnswers = member?.joinAnswers && typeof member.joinAnswers === 'object'
      ? member.joinAnswers
      : {};
    return normalizeDisplayValue(getJoinAnswerValue(joinAnswers[filter.fieldId]));
  }

  const profileFields = member?.profileFields && typeof member.profileFields === 'object'
    ? member.profileFields
    : {};
  return normalizeDisplayValue(profileFields[filter.fieldId]);
}

function memberMatchesFilter(member = {}, filter = {}) {
  if (filter.kind === 'tag_category') {
    const memberTags = new Set(normalizeMemberTags(member?.tags).map((tagName) => tagName.toLowerCase()));
    const categoryTagNames = Array.isArray(filter.categoryTagNames) ? filter.categoryTagNames : [];

    if (filter.matchMode === 'tag_category_none') {
      return !categoryTagNames.some((tagName) => memberTags.has(tagName.toLowerCase()));
    }

    return memberTags.has(filter.valueLower);
  }

  const actualRaw = getFilterValue(member, filter);
  const actualLower = actualRaw.toLowerCase();

  if (filter.matchMode === 'is_blank') return actualRaw.trim() === '';
  if (filter.matchMode === 'is_not_blank') return actualRaw.trim() !== '';
  if (filter.matchMode === 'equals') return actualLower === filter.valueLower;
  if (filter.matchMode === 'number_equals') {
    const actualNumber = Number(actualRaw);
    return Number.isFinite(actualNumber) && actualNumber === filter.valueNumber;
  }
  if (filter.matchMode === 'date_equals') return actualRaw === filter.value;
  return actualLower.includes(filter.valueLower);
}

function getFilterLabel(filter = {}) {
  if (filter.matchMode === 'tag_category_none') return `${filter.fieldLabel} has no tag`;
  if (filter.matchMode === 'is_blank') return `${filter.fieldLabel} is blank`;
  if (filter.matchMode === 'is_not_blank') return `${filter.fieldLabel} is filled in`;
  if (filter.matchMode === 'contains') return `${filter.fieldLabel} contains ${filter.value}`;
  return `${filter.fieldLabel} = ${filter.value}`;
}

export default function ProfileSearchModal({
  isOpen,
  onClose,
  teamId,
  members = [],
  profileConfig,
  joinFormConfig,
  allTags = [],
  teamName = '',
  canEditProfiles = false,
  onOpenMember,
}) {
  const [search, setSearch] = useState('');
  const [selectedFilterKey, setSelectedFilterKey] = useState('');
  const [selectedMatchMode, setSelectedMatchMode] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [filters, setFilters] = useState([]);

  const qualificationFields = useMemo(() => (profileConfig?.fields || []).filter(field => field.type === 'qualification'), [profileConfig]);
  const qualificationData = useQualificationRecords(teamId, null, Boolean(isOpen && canEditProfiles && qualificationFields.length), true);
  const searchMembers = useMemo(() => {
    if (!canEditProfiles || !qualificationFields.length || !teamId) return members;
    const byKey = new Map(qualificationData.records.map(record => [`${record.memberId}~${record.qualificationId}`, record]));
    return members.map(member => ({ ...member, profileFields: { ...member.profileFields,
      ...Object.fromEntries(qualificationFields.map(field => [field.id, qualificationData.loaded && !qualificationData.error ? QUALIFICATION_STATUS_LABELS[qualificationStatus(byKey.get(`${member.id}~${field.id}`), qualificationData.today)] : 'Loading…'])) } }));
  }, [members, canEditProfiles, qualificationFields, teamId, qualificationData.records, qualificationData.loaded, qualificationData.error, qualificationData.today]);
  const profileLabelById = useMemo(() => {
    const map = new Map();
    const fields = Array.isArray(profileConfig?.fields) ? profileConfig.fields : [];
    fields.forEach((field) => {
      if (typeof field?.id !== 'string') return;
      map.set(field.id, normalizeText(field?.label) || field.id);
    });
    return map;
  }, [profileConfig]);

  const availableProfileFields = useMemo(() => {
    const byId = new Map();
    const configured = Array.isArray(profileConfig?.fields) ? profileConfig.fields : [];

    configured.forEach((field) => {
      const id = typeof field?.id === 'string' ? field.id.trim() : '';
      if (!id) return;
      byId.set(id, {
        id,
        label: normalizeText(field?.label) || id,
        type: normalizeFieldType(field?.type),
        options: normalizeOptions(field?.options),
      });
    });

    searchMembers.forEach((member) => {
      const profileFields = member?.profileFields && typeof member.profileFields === 'object'
        ? member.profileFields
        : {};
      Object.entries(profileFields).forEach(([fieldId, rawValue]) => {
        const id = normalizeText(fieldId);
        if (!id) return;
        if (!byId.has(id)) {
          byId.set(id, {
            id,
            label: profileLabelById.get(id) || id,
            type: 'text',
            options: [],
          });
        }

        const existing = byId.get(id);
        const optionValue = normalizeDisplayValue(rawValue);
        if (optionValue && existing?.type === 'select') {
          byId.set(id, {
            ...existing,
            options: mergeOptions(existing.options, [optionValue]),
          });
        }
      });
    });

    return Array.from(byId.values())
      .map((field) => ({
        ...field,
        key: `profile:${field.id}`,
        kind: 'profile',
        options: normalizeOptions(field.options),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [searchMembers, profileConfig, profileLabelById]);

  const availableJoinQuestions = useMemo(() => {
    const byId = new Map();
    const configured = Array.isArray(joinFormConfig?.questions) ? joinFormConfig.questions : [];

    configured.forEach((question) => {
      const id = typeof question?.id === 'string' ? question.id.trim() : '';
      if (!id || ['pricingTags', 'pricingTag'].includes(id)) return;
      const type = normalizeFieldType(question?.type);
      byId.set(id, {
        id,
        label: normalizeText(question?.label) || id,
        type,
        options: type === 'select'
          ? mergeOptions(question?.options, ['yesno', 'checkbox'].includes(normalizeText(question?.type).toLowerCase()) ? ['yes', 'no'] : [])
          : [],
      });
    });

    searchMembers.forEach((member) => {
      const joinAnswers = member?.joinAnswers && typeof member.joinAnswers === 'object'
        ? member.joinAnswers
        : {};
      Object.entries(joinAnswers).forEach(([questionId, answer]) => {
        const id = normalizeText(questionId);
        if (!id) return;
        const type = normalizeFieldType(answer?.type);
        const value = normalizeDisplayValue(getJoinAnswerValue(answer));
        if (!byId.has(id)) {
          byId.set(id, {
            id,
            label: normalizeText(answer?.label) || id,
            type,
            options: type === 'select' ? mergeOptions([], [value]) : [],
          });
          return;
        }

        const existing = byId.get(id);
        if (value && existing?.type === 'select') {
          byId.set(id, {
            ...existing,
            options: mergeOptions(existing.options, [value]),
          });
        }
      });
    });

    return Array.from(byId.values())
      .map((field) => ({
        ...field,
        key: `join:${field.id}`,
        kind: 'join',
        options: normalizeOptions(field.options),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [joinFormConfig, searchMembers]);

  const availableTagCategories = useMemo(() => {
    const categories = new Map();

    (Array.isArray(allTags) ? allTags : [])
      .filter((tag) => tag?.type !== 'ageRule')
      .forEach((tag) => {
        const name = normalizeText(tag?.name);
        if (!name) return;

        const category = normalizeText(tag?.category) || 'Uncategorised';
        if (!categories.has(category)) {
          categories.set(category, []);
        }

        categories.get(category).push(name);
      });

    return Array.from(categories.entries())
      .map(([category, names]) => {
        const options = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
        return {
          key: `tag_category:${category}`,
          kind: 'tag_category',
          id: category,
          label: category,
          type: 'select',
          options: [...options, TAG_CATEGORY_NONE_OPTION],
          categoryTagNames: options,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allTags]);

  const filterGroups = useMemo(() => {
    const groups = [];
    if (availableTagCategories.length > 0) {
      groups.push({
        label: 'Tag categories',
        options: availableTagCategories,
      });
    }
    if (availableProfileFields.length > 0) {
      groups.push({ label: 'Profile fields', options: availableProfileFields });
    }
    if (availableJoinQuestions.length > 0) {
      groups.push({ label: 'Join questions', options: availableJoinQuestions });
    }
    return groups;
  }, [availableJoinQuestions, availableProfileFields, availableTagCategories]);

  const availableFilters = useMemo(
    () => filterGroups.flatMap((group) => group.options),
    [filterGroups]
  );

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelectedFilterKey('');
      setSelectedMatchMode('');
      setFilterValue('');
      setFilters([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedFilterKey && availableFilters.some((field) => field.key === selectedFilterKey)) return;
    setSelectedFilterKey(availableFilters[0]?.key || '');
  }, [availableFilters, selectedFilterKey]);

  useEffect(() => {
    setSelectedMatchMode('');
    setFilterValue('');
  }, [selectedFilterKey]);

  const selectedFilter = useMemo(
    () => availableFilters.find((field) => field.key === selectedFilterKey) || null,
    [availableFilters, selectedFilterKey]
  );

  const selectedFilterInputKind = useMemo(() => {
    if (!selectedFilter) return 'text';
    if (selectedFilter.type === 'date') return 'date';
    if (selectedFilter.type === 'number') return 'number';
    if (selectedFilter.type === 'select' && (selectedFilter.options || []).length > 0) return 'select';
    return 'text';
  }, [selectedFilter]);

  const selectedFilterMatchOptions = useMemo(() => {
    if (!selectedFilter) return [];
    if (selectedFilter.kind === 'tag_category') {
      return [{ value: 'tag_category_equals', label: 'is' }];
    }
    const options = [];
    if (selectedFilter.type === 'number') options.push({ value: 'number_equals', label: 'equals' });
    else if (selectedFilter.type === 'date') options.push({ value: 'date_equals', label: 'is' });
    else if (selectedFilter.type === 'select') options.push({ value: 'equals', label: 'is' });
    else options.push({ value: 'contains', label: 'contains' });

    options.push(
      { value: 'is_not_blank', label: 'is filled in' },
      { value: 'is_blank', label: 'is blank' }
    );
    return options;
  }, [selectedFilter]);

  const activeMatchMode = useMemo(() => {
    if (!selectedFilter) return '';
    if (selectedFilter.kind === 'tag_category') return 'tag_category_equals';
    const values = selectedFilterMatchOptions.map((option) => option.value);
    if (selectedMatchMode && values.includes(selectedMatchMode)) return selectedMatchMode;
    return values[0] || '';
  }, [selectedFilter, selectedFilterMatchOptions, selectedMatchMode]);

  const filterNeedsValue = selectedFilter?.kind === 'tag_category' || !BLANK_MATCH_MODES.has(activeMatchMode);

  const filteredMembers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return searchMembers.filter((member) => {
      if (needle && !buildMemberSearchText(member, profileLabelById).includes(needle)) return false;
      if (filters.length > 0) {
        for (const filter of filters) {
          if (!memberMatchesFilter(member, filter)) return false;
        }
      }
      return true;
    });
  }, [filters, searchMembers, profileLabelById, search]);

  const handleExportCsv = () => {
    if (filteredMembers.length === 0) return;

    const columns = [
      { header: 'Name', getValue: (member) => normalizeText(member?.name) },
      { header: 'Tags', getValue: (member) => normalizeMemberTags(member?.tags).join(', ') },
      ...availableProfileFields.map((field) => ({
        header: `Profile: ${field.label}`,
        getValue: (member) => normalizeDisplayValue(member?.profileFields?.[field.id]),
      })),
      ...availableJoinQuestions.map((field) => ({
        header: `Join: ${field.label}`,
        getValue: (member) => {
          const joinAnswers = member?.joinAnswers && typeof member.joinAnswers === 'object'
            ? member.joinAnswers
            : {};
          return normalizeDisplayValue(getJoinAnswerValue(joinAnswers[field.id]));
        },
      })),
    ];

    const lines = [
      columns.map((column) => csvEscape(column.header)).join(','),
      ...filteredMembers.map((member) => columns.map((column) => csvEscape(column.getValue(member))).join(',')),
    ];

    triggerCsvDownload(`${lines.join('\n')}\n`, buildFileName(teamName));
  };

  const handleAddFilter = () => {
    if (!selectedFilter) return;
    const rawInputValue = normalizeText(filterValue);
    const valueRaw = BLANK_MATCH_MODES.has(activeMatchMode) ? '' : rawInputValue;
    if (filterNeedsValue && !valueRaw) return;

    let matchMode = activeMatchMode || 'contains';
    if (selectedFilter.kind === 'tag_category') matchMode = valueRaw === TAG_CATEGORY_NONE_OPTION ? 'tag_category_none' : 'tag_category_equals';

    const key = `${selectedFilter.key}::${matchMode}::${valueRaw.toLowerCase()}`;
    if (filters.some((filter) => filter.key === key)) return;

    const nextFilter = {
      key,
      kind: selectedFilter.kind,
      fieldId: selectedFilter.id,
      fieldLabel: selectedFilter.label || selectedFilter.id,
      fieldType: selectedFilter.type || 'text',
      value: valueRaw === TAG_CATEGORY_NONE_OPTION ? 'No tag in this category' : valueRaw,
      valueLower: valueRaw.toLowerCase(),
      matchMode,
      categoryTagNames: selectedFilter.kind === 'tag_category' ? selectedFilter.categoryTagNames || [] : undefined,
    };

    if (matchMode === 'number_equals') {
      const valueNumber = Number(valueRaw);
      if (!Number.isFinite(valueNumber)) return;
      nextFilter.valueNumber = valueNumber;
    }

    setFilters((prev) => [...prev, nextFilter]);
    setFilterValue('');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content profile-search-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Member Search</h2>
        {qualificationData.error && <p role="alert">{qualificationData.error}</p>}
        <p className="text-muted" style={{ marginTop: '-0.25rem' }}>
          Search names, tags, profile fields, or join answers. Add filters to build lists like senior or junior members.
        </p>

        <div className="profile-search-controls">
          <input
            type="text"
            className="input"
            placeholder="Search members, answers, or questions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div>
            <div className="text-muted" style={{ fontSize: '0.8rem', marginBottom: '0.35rem' }}>
              Add a filter
            </div>
            {availableFilters.length > 0 ? (
              <>
                <div className="profile-search-filter-row">
                  <select
                    className="input"
                    value={selectedFilterKey}
                    onChange={(e) => setSelectedFilterKey(e.target.value)}
                  >
                    {filterGroups.map((group) => (
                      <optgroup key={group.label} label={group.label}>
                        {group.options.map((field) => (
                          <option key={field.key} value={field.key}>
                            {field.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>

                  {selectedFilterMatchOptions.length > 1 && (
                    <select
                      className="input"
                      value={activeMatchMode}
                      onChange={(e) => setSelectedMatchMode(e.target.value)}
                      aria-label="Filter match type"
                    >
                      {selectedFilterMatchOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}

                  {filterNeedsValue && selectedFilterInputKind === 'select' ? (
                    <select
                      className="input"
                      value={filterValue}
                      onChange={(e) => setFilterValue(e.target.value)}
                    >
                      <option value="">{CHOOSE_OPTION_LABEL}</option>
                      {(selectedFilter?.options || []).map((option) => (
                        <option key={option} value={option}>
                          {option === TAG_CATEGORY_NONE_OPTION ? 'No tag in this category' : option}
                        </option>
                      ))}
                    </select>
                  ) : filterNeedsValue ? (
                    <input
                      type={selectedFilterInputKind === 'date' ? 'date' : (selectedFilterInputKind === 'number' ? 'number' : 'text')}
                      className="input"
                      placeholder={
                        selectedFilterInputKind === 'date'
                          ? ''
                          : selectedFilterInputKind === 'number'
                            ? 'Enter a number…'
                            : 'Enter a response…'
                      }
                      value={filterValue}
                      onChange={(e) => setFilterValue(e.target.value)}
                    />
                  ) : null}

                  <button type="button" className="btn btn--secondary btn--xs" onClick={handleAddFilter}>
                    Add filter
                  </button>
                  {filters.length > 0 && (
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => setFilters([])}>
                      Clear filters
                    </button>
                  )}
                </div>

                {filters.length > 0 && (
                  <div className="profile-search-active-filters">
                    {filters.map((filter) => (
                      <button
                        key={filter.key}
                        type="button"
                        className="btn btn--secondary btn--xs"
                        onClick={() => setFilters((prev) => prev.filter((item) => item.key !== filter.key))}
                        title="Remove filter"
                      >
                        {getFilterLabel(filter)} (remove)
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                No tags, profile fields, or join answers are available yet.
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}>
            <strong>Results ({filteredMembers.length})</strong>
            <button
              type="button"
              className="btn btn--secondary btn--xs"
              onClick={handleExportCsv}
              disabled={filteredMembers.length === 0}
            >
              Export CSV
            </button>
          </div>
        </div>

        <div className="profile-search-results">
          {filteredMembers.length === 0 ? (
            <p className="text-muted">No members match that search.</p>
          ) : (
            <ul className="list-none divide-y" style={{ margin: 0, padding: 0 }}>
              {filteredMembers.map((member) => {
                const answerRows = buildAnswerRows(member, profileLabelById);
                const memberTags = normalizeMemberTags(member?.tags);
                const needle = search.trim().toLowerCase();
                const matchingRows = needle
                  ? answerRows.filter((row) => `${row.source} ${row.label} ${row.value}`.toLowerCase().includes(needle)).slice(0, 3)
                  : filters
                    .filter((filter) => ['profile', 'join'].includes(filter.kind))
                    .map((filter) => answerRows.find((row) => row.kind === filter.kind && row.fieldId === filter.fieldId))
                    .filter(Boolean)
                    .slice(0, 3);

                return (
                  <li key={member.id} className="profile-search-result">
                    <div className="profile-search-result__summary">
                      <div className="profile-search-result__main">
                        <strong>{member.name}</strong>
                        {memberTags.length > 0 && (
                          <div className="profile-search-tags" aria-label={`${member.name} tags`}>
                            {memberTags.map((tagName) => (
                              <span key={`${member.id}-tag-${tagName}`} className="tag-chip">{tagName}</span>
                            ))}
                          </div>
                        )}
                        {matchingRows.length > 0 && (
                          <div className="profile-search-match-list">
                            {matchingRows.map((row) => (
                              <div key={`${member.id}-match-${row.key}`}>
                                <span>{row.source}:</span> {row.label}: {row.value || EMPTY_VALUE_LABEL}
                              </div>
                            ))}
                          </div>
                        )}
                        {answerRows.length > 0 && (
                          <details className="profile-search-result__details">
                            <summary>Profile and join answers ({answerRows.length})</summary>
                            <dl className="profile-search-answer-list">
                              {answerRows.map((row) => (
                                <div key={`${member.id}-row-${row.key}`}>
                                  <dt>{row.source}: {row.label}</dt>
                                  <dd>{row.value || EMPTY_VALUE_LABEL}</dd>
                                </div>
                              ))}
                            </dl>
                          </details>
                        )}
                      </div>
                      {canEditProfiles && typeof onOpenMember === 'function' && (
                        <button
                          type="button"
                          className="btn btn--secondary btn--xs"
                          onClick={() => onOpenMember(member)}
                        >
                          Edit Profile
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="form-actions" style={{ marginTop: '1rem' }}>
          <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

ProfileSearchModal.propTypes = {
  teamId: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  members: PropTypes.array,
  profileConfig: PropTypes.shape({
    enabled: PropTypes.bool,
    fields: PropTypes.array,
  }),
  joinFormConfig: PropTypes.shape({
    enabled: PropTypes.bool,
    questions: PropTypes.array,
  }),
  allTags: PropTypes.array,
  teamName: PropTypes.string,
  canEditProfiles: PropTypes.bool,
  onOpenMember: PropTypes.func,
};
